import { Fragment, useCallback, useId, useState, type ReactNode } from "react";
import { useStudioStore } from "../../store/studio-store";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ChevronDown,
  ChevronUp,
  Circle,
  LayoutTemplate,
  LogIn,
  LogOut,
  Settings,
  ArrowLeft,
} from "lucide-react";
import {
  findStudioAuthorityTask,
  getStudioRenewalRailItems,
  resolveStudioAuthorityDomain,
  type StudioRenewalRailItem,
} from "./studio-authority-workspaces";
import { useStudioProjectOutputKind } from "./components/artifact-mode-switcher";
import { activateStudioAuthorityTask } from "./studio-authority-activation";
import { useStudioLocale } from "@/i18n";
import { getCoogoIdeRailLabel } from "./coogo-ide-renewal-copy";
import { RAIL_ITEMS, type RailItemId } from "./types";
import { getStudioContextualTaskCopy } from "./contextual-tasks/studio-contextual-task-copy";
import type { StudioContextualTaskItem } from "./contextual-tasks/studio-contextual-task-menu";
import { useStudioContextualTasks } from "./contextual-tasks/use-studio-contextual-tasks";

interface PresentedRailItem {
  id: string;
  taskId?: string;
  railItem: RailItemId;
  icon: StudioRenewalRailItem["icon"];
  dividerBefore?: boolean;
  label: string;
}

/** One resolved contextual task adapted to the rail's icon + localized label. */
interface ContextualRailItem {
  taskId: string;
  railItem: RailItemId;
  icon: StudioRenewalRailItem["icon"];
  label: string;
  disabled: boolean;
  disabledReasonText: string | null;
}

const RAIL_TASK_BUTTON_CLASS =
  "flex h-[52px] w-[52px] flex-col items-center justify-center gap-[3px] rounded-[9px] px-1 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function ContextualRailButton({
  item,
  isActive,
  reasonId,
  onActivate,
}: {
  item: ContextualRailItem;
  isActive: boolean;
  reasonId: string;
  onActivate: (taskId: string) => void;
}) {
  const Icon = item.icon;
  return (
    <div className="flex w-full flex-col items-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={item.label}
            aria-current={isActive ? "page" : undefined}
            aria-disabled={item.disabled ? "true" : undefined}
            aria-describedby={
              item.disabled && item.disabledReasonText ? reasonId : undefined
            }
            // A11Y-01: `aria-disabled` instead of the `disabled` attribute.
            // A natively disabled button is removed from the tab order, so the
            // `aria-describedby` reason ("The original target is no longer
            // available.") is unreachable to a keyboard/screen-reader user —
            // exactly the audience the reason exists for. Keeping the button
            // focusable makes the explanation announceable; activation is
            // blocked below, so the task stays non-activatable either way.
            data-testid={`authority-task-${item.taskId}`}
            onClick={() => {
              if (item.disabled) return;
              onActivate(item.taskId);
            }}
            className={`${RAIL_TASK_BUTTON_CLASS} ${
              item.disabled
                ? "cursor-not-allowed text-muted-foreground/50"
                : isActive
                  ? "bg-primary/10 font-bold text-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            <Icon
              className="h-4 w-4 shrink-0"
              strokeWidth={isActive ? 2 : 1.6}
            />
            <span className="max-w-full truncate text-[9px] font-semibold leading-none">
              {item.label}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
      {item.disabled && item.disabledReasonText ? (
        <span
          id={reasonId}
          className="mt-0.5 w-full text-balance px-0.5 text-center text-[8px] leading-tight text-muted-foreground/80"
        >
          {item.disabledReasonText}
        </span>
      ) : null}
    </div>
  );
}

function RailIconButton({
  label,
  expandedLabel,
  onClick,
  isActive = false,
  variant = "default",
  children,
  testId,
}: {
  label: string;
  expandedLabel?: string;
  onClick: () => void;
  isActive?: boolean;
  variant?: "default" | "destructive";
  children: ReactNode;
  testId?: string;
}) {
  const baseColor = isActive
    ? "border border-border/70 bg-muted text-foreground font-semibold shadow-sm"
    : variant === "destructive"
      ? "text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          aria-current={isActive ? "page" : undefined}
          data-testid={testId}
          data-collaboration-role={isActive ? "cogo" : undefined}
          className={`flex h-11 w-11 min-w-0 items-center justify-center gap-3 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 2xl:w-full 2xl:justify-start 2xl:px-3 ${baseColor}`}
        >
          {children}
          {expandedLabel ? (
            <span className="studio-ellipsis-flex hidden text-xs font-medium 2xl:block">
              {expandedLabel}
            </span>
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

function AccountMenu({
  displayName,
  email,
  initial,
  expanded,
  onBackToProjects,
  onBrowseTemplates,
  onProjectSettings,
  onSignOut,
}: {
  displayName: string;
  email: string;
  initial: string;
  expanded: boolean;
  onBackToProjects: () => void;
  onBrowseTemplates: () => void;
  onProjectSettings: () => void;
  onSignOut: () => void;
}) {
  const { strings } = useStudioLocale();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="creator-nav-profile"
          aria-label={`${strings.navigation.account}: ${email}`}
          className={
            expanded
              ? "group flex w-full items-center gap-3 rounded-xl px-1.5 py-2 text-left transition-colors hover:bg-foreground/5"
              : "group flex justify-center py-1"
          }
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-950/15 bg-slate-950 text-xs font-bold text-white shadow-sm transition-colors group-hover:bg-slate-800 dark:border-slate-50/20 dark:bg-slate-50 dark:text-slate-950">
            {initial}
          </span>
          {expanded && (
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="studio-ellipsis text-xs font-semibold text-foreground">
                {displayName}
              </span>
              <span className="studio-ellipsis mt-0.5 text-[10px] leading-none text-muted-foreground/60">
                {email}
              </span>
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="end"
        sideOffset={10}
        className="w-56 p-2"
      >
        <div className="border-b border-border/60 px-2 pb-2 pt-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {strings.navigation.account}
          </div>
          <div className="studio-ellipsis mt-1 text-xs font-semibold text-foreground">
            {displayName}
          </div>
          <div className="studio-ellipsis text-[11px] text-muted-foreground">
            {email}
          </div>
        </div>
        <button
          type="button"
          data-testid="creator-nav-back-to-projects"
          onClick={onBackToProjects}
          className="mt-1 flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          {strings.navigation.projects}
        </button>
        <button
          type="button"
          onClick={onBrowseTemplates}
          className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-muted"
        >
          <LayoutTemplate className="h-4 w-4" strokeWidth={1.5} />
          {strings.navigation.templates}
        </button>
        <button
          type="button"
          onClick={onProjectSettings}
          className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-muted"
        >
          <Settings className="h-4 w-4" strokeWidth={1.5} />
          {strings.navigation.projectSettings}
        </button>
        <button
          type="button"
          onClick={onSignOut}
          data-testid="creator-nav-signout"
          className="mt-1 flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.5} />
          {strings.navigation.signOut}
        </button>
      </PopoverContent>
    </Popover>
  );
}

function SignInControl({ expanded }: { expanded: boolean }) {
  const { strings } = useStudioLocale();
  const handleSignIn = useCallback(() => {
    window.history.pushState(null, "", "/login");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, []);

  if (!expanded) {
    return (
      <RailIconButton
        label={strings.navigation.signIn}
        onClick={handleSignIn}
        testId="creator-nav-signin"
      >
        <LogIn className="h-5 w-5" strokeWidth={1.5} />
      </RailIconButton>
    );
  }

  return (
    <button
      type="button"
      onClick={handleSignIn}
      data-testid="creator-nav-signin"
      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
    >
      <LogIn className="h-4 w-4 shrink-0" strokeWidth={1.5} />
      <span className="studio-ellipsis-flex">{strings.navigation.signIn}</span>
    </button>
  );
}

export function GroupedRail() {
  const { locale, strings } = useStudioLocale();
  const activeRailItem = useStudioStore((s) => s.activeRailItem);
  const setActiveRailItem = useStudioStore((s) => s.setActiveRailItem);
  const setInspectorPanelOpen = useStudioStore((s) => s.setInspectorPanelOpen);
  const user = useStudioStore((s) => s.user);
  const signOut = useStudioStore((s) => s.signOut);
  const currentProjectId = useStudioStore((s) => s.currentProjectId);
  const closeProject = useStudioStore((s) => s.closeProject);
  const studioPersona = useStudioStore((s) => s.studioPersona);
  const chatContext = useStudioStore((s) => s.chatContext);
  const outputKind = useStudioProjectOutputKind();

  const displayName = user?.user_metadata?.full_name ?? user?.email ?? "";
  const email = user?.email ?? "";
  const initial = displayName.charAt(0).toUpperCase() || "C";

  const authorityDomain =
    chatContext?.authorityDomain ??
    resolveStudioAuthorityDomain(activeRailItem) ??
    "primary";
  const renewalRailItems = getStudioRenewalRailItems(
    authorityDomain,
    outputKind,
  );
  const coreRailItems: PresentedRailItem[] = renewalRailItems.map((item) => ({
    ...item,
    label: getCoogoIdeRailLabel(locale, item.id),
  }));
  const advancedLabels: Partial<Record<RailItemId, string>> = {
    library: strings.navigation.details,
    map: strings.navigation.map,
    code: strings.navigation.code,
  };
  const advancedRailItems: PresentedRailItem[] =
    studioPersona === "developer"
      ? RAIL_ITEMS.filter((item) =>
          ["library", "map", "code"].includes(item.id),
        ).map((item, index) => ({
          id: `advanced.${item.id}`,
          railItem: item.id,
          icon: item.icon,
          dividerBefore: index === 0,
          label: advancedLabels[item.id] ?? item.label,
        }))
      : [];
  const presentedRailItems: PresentedRailItem[] = [
    ...coreRailItems.filter((item) => item.railItem !== "settings"),
    ...advancedRailItems,
    ...coreRailItems.filter((item) => item.railItem === "settings"),
  ];
  const activeAuthorityTask = findStudioAuthorityTask(
    authorityDomain,
    chatContext?.authorityView,
    outputKind,
  );

  // Creators get the contextual task menu (Recommended + More) over the same
  // authority catalog the developer rail lists flat. Every other persona,
  // domain, and output kind keeps its existing full-rail presentation.
  const contextualMenu = useStudioContextualTasks({ domain: authorityDomain });
  const contextualCopy = getStudioContextualTaskCopy(locale);
  const contextualRailEnabled =
    studioPersona === "creator" &&
    authorityDomain === "primary" &&
    outputKind === "app";
  const [moreOpen, setMoreOpen] = useState(false);
  const railBaseId = useId();
  const moreRegionId = `${railBaseId}-more`;

  const renewalByTaskId = new Map(
    renewalRailItems.map((item) => [item.taskId, item] as const),
  );
  const toContextualRailItem = (
    item: StudioContextualTaskItem,
  ): ContextualRailItem => {
    const renewal = renewalByTaskId.get(item.task.id);
    return {
      taskId: item.task.id,
      railItem: renewal?.railItem ?? item.task.railItem,
      icon: renewal?.icon ?? Circle,
      label: renewal ? getCoogoIdeRailLabel(locale, renewal.id) : item.task.label,
      disabled: item.disabled,
      disabledReasonText: item.disabledReason
        ? contextualCopy.disabled[item.disabledReason]
        : null,
    };
  };
  const primaryContextualItems = contextualMenu.primaryTasks.map(
    toContextualRailItem,
  );
  const moreContextualItems = contextualMenu.moreTasks.map(
    toContextualRailItem,
  );
  const recommendationText = contextualMenu.recommendationReason
    ? contextualCopy.reason[contextualMenu.recommendationReason]
    : null;

  const isContextualItemActive = (item: ContextualRailItem) =>
    activeAuthorityTask.id === item.taskId && activeRailItem === item.railItem;

  const handleLogoClick = useCallback(() => {
    if (currentProjectId) closeProject();
    window.history.pushState(null, "", "/studio");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [currentProjectId, closeProject]);

  const handleTemplatesClick = useCallback(() => {
    if (currentProjectId) closeProject();
    window.history.pushState(null, "", "/studio/templates");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [currentProjectId, closeProject]);

  const handleProjectSettingsClick = useCallback(() => {
    activateStudioAuthorityTask({
      domain: authorityDomain,
      outputKind,
      requestedView: "settings",
    });
  }, [authorityDomain, outputKind]);

  const handleRailItemClick = useCallback(
    (id: StudioRenewalRailItem["railItem"]) => {
      setActiveRailItem(id);
      // The right rail is the user's intent loop, not a preview-only chat.
      // Keep it open across Pages, Logic, Data, Proof, and Release surfaces so
      // users can ask, review, approve, or revise without losing context. The
      // inspector/context sub-mode is reset because it can obscure the newly
      // selected surface.
      setInspectorPanelOpen(false);
    },
    [setActiveRailItem, setInspectorPanelOpen],
  );

  const handleContextualTaskClick = useCallback(
    (taskId: string) => {
      activateStudioAuthorityTask({
        domain: authorityDomain,
        outputKind,
        requestedView: taskId,
      });
    },
    [authorityDomain, outputKind],
  );

  const handleRenewalRailClick = useCallback(
    (item: PresentedRailItem) => {
      if (item.taskId) {
        activateStudioAuthorityTask({
          domain: authorityDomain,
          outputKind,
          requestedView: item.taskId,
        });
        return;
      }
      handleRailItemClick(item.railItem);
    },
    [authorityDomain, handleRailItemClick, outputKind],
  );

  const handleSignOut = useCallback(async () => {
    if (currentProjectId) closeProject();
    await signOut();
    window.history.replaceState(null, "", "/studio");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [currentProjectId, closeProject, signOut]);

  return (
    <TooltipProvider delayDuration={300}>
      <div
        data-testid="grouped-rail"
        data-authority-domain={authorityDomain}
        className="flex w-16 shrink-0 select-none flex-col items-center border-r border-border bg-background py-2"
      >
        <div className="flex w-full flex-1 flex-col items-center gap-1 overflow-y-auto">
          {contextualRailEnabled ? (
            <>
              <section
                aria-label={contextualCopy.recommended}
                className="flex w-full flex-col items-center gap-1"
              >
                {primaryContextualItems.map((item) => (
                  <Fragment key={item.taskId}>
                    <ContextualRailButton
                      item={item}
                      isActive={isContextualItemActive(item)}
                      reasonId={`${railBaseId}-reason-${item.taskId}`}
                      onActivate={handleContextualTaskClick}
                    />
                    {item.taskId === contextualMenu.recommendedTaskId &&
                    recommendationText ? (
                      <p
                        data-testid="creator-rail-recommendation-reason"
                        className="w-full text-balance px-0.5 text-center text-[8px] leading-tight text-muted-foreground"
                      >
                        {recommendationText}
                      </p>
                    ) : null}
                  </Fragment>
                ))}
              </section>
              {moreContextualItems.length > 0 ? (
                <div className="flex w-full flex-col items-center gap-1">
                  <div aria-hidden className="my-1 h-px w-10 shrink-0 bg-border" />
                  <button
                    type="button"
                    data-testid="creator-rail-more-toggle"
                    aria-label={
                      moreOpen ? contextualCopy.showLess : contextualCopy.more
                    }
                    aria-expanded={moreOpen}
                    aria-controls={moreRegionId}
                    onClick={() => setMoreOpen((open) => !open)}
                    className="flex w-[52px] flex-col items-center justify-center gap-[3px] rounded-[9px] px-1 py-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {moreOpen ? (
                      <ChevronUp className="h-4 w-4 shrink-0" strokeWidth={1.6} />
                    ) : (
                      <ChevronDown
                        className="h-4 w-4 shrink-0"
                        strokeWidth={1.6}
                      />
                    )}
                    <span className="max-w-full truncate text-[9px] font-semibold leading-none">
                      {moreOpen ? contextualCopy.showLess : contextualCopy.more}
                    </span>
                  </button>
                  <div
                    id={moreRegionId}
                    className="flex w-full flex-col items-center gap-1"
                  >
                    {moreOpen
                      ? moreContextualItems.map((item) => (
                          <ContextualRailButton
                            key={item.taskId}
                            item={item}
                            isActive={isContextualItemActive(item)}
                            reasonId={`${railBaseId}-reason-${item.taskId}`}
                            onActivate={handleContextualTaskClick}
                          />
                        ))
                      : null}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
          {contextualRailEnabled ? null : presentedRailItems.map((item) => {
            const Icon = item.icon;
            const label = item.label;
            const isActive = item.taskId
              ? activeAuthorityTask.id === item.taskId &&
                activeRailItem === item.railItem
              : activeRailItem === item.railItem;
            return (
              <div
                key={item.id}
                className={`flex w-full flex-col items-center ${item.id.endsWith(".settings") ? "mt-auto" : ""}`}
              >
                {item.dividerBefore ? (
                  <div
                    aria-hidden
                    className="my-1 h-px w-10 shrink-0 bg-border"
                  />
                ) : null}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={label}
                      aria-current={isActive ? "page" : undefined}
                      data-testid={
                        item.taskId
                          ? `authority-task-${item.taskId}`
                          : `creator-nav-${item.railItem}`
                      }
                      onClick={() => handleRenewalRailClick(item)}
                      className={`flex h-[52px] w-[52px] flex-col items-center justify-center gap-[3px] rounded-[9px] px-1 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isActive ? "bg-primary/10 font-bold text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                    >
                      <Icon
                        className="h-4 w-4 shrink-0"
                        strokeWidth={isActive ? 2 : 1.6}
                      />
                      <span className="max-w-full truncate text-[9px] font-semibold leading-none">
                        {label}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">{label}</TooltipContent>
                </Tooltip>
              </div>
            );
          })}
        </div>

        {/* Bottom profile block: avatar owns account/project shortcuts. */}
        <div className="mt-auto flex w-full shrink-0 flex-col items-center gap-3 border-t border-border/60 pt-4">
          {user ? (
            <AccountMenu
              displayName={displayName}
              email={email}
              initial={initial}
              expanded={false}
              onBackToProjects={handleLogoClick}
              onBrowseTemplates={handleTemplatesClick}
              onProjectSettings={handleProjectSettingsClick}
              onSignOut={() => void handleSignOut()}
            />
          ) : (
            <SignInControl expanded={false} />
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
